// @flow

import AuthHandshake from 'app/common/lib/AuthHandshake';
import context from 'app/common/lib/context';
import Logging from 'app/common/lib/Logging';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import templateConst from 'app/common/lib/templateConst';
import trackLoginInFailure from 'app/common/react/components/growth/unauth/signup/SignupUtil';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { LoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import { setUserOpenUnauth } from 'app/common/react/components/growth/unauth/open/hybrid/hybridExperimentUtils';
import { SIGNUP_METHOD } from 'app/packages/pinterest-authentication/registration';
import { type UnauthSeoExperiments } from 'app/common/lib/react/withUnauthSeoExperiments';
import { unregisterServiceWorker } from 'app/common/lib/swUtil';

const log = Logging.getLogger('UserLogin');
const authHandshakeLog = Logging.getLogger('AuthHandshake');

type LoggingData = {|
  container: string,
  hybridTier: string,
  page: string,
  signupSource?: string,
  is_business?: boolean,
|};

const getLoginType = function(creds: LoginCreds): string {
  if (creds.mfa_token) {
    // this sends also username_or_email/password, so it needs to be first
    return 'mfa_token';
  } else if (creds.mfa_resend) {
    return 'mfa_resend';
  } else if (creds.facebook_id) {
    return SIGNUP_METHOD.FACEBOOK;
  } else if (creds.google_open_id_token) {
    return SIGNUP_METHOD.GOOGLE_ONE_TAP;
  } else if (creds.gplus_id_token) {
    return SIGNUP_METHOD.GOOGLE;
  } else if (creds.phone_number) {
    return 'phone_number';
  } else if (creds.username_or_email) {
    return SIGNUP_METHOD.EMAIL;
  } else {
    return SIGNUP_METHOD.OTHER;
  }
};

const trackLogin = function(creds: LoginCreds, loggingData: LoggingData) {
  let referrer = 'referrer_unknown';
  try {
    // TODO (jeffc): getInitModuleName does not work on react only pages, find another way to get main module name
    const referrer_url = document.referrer;
    if (!referrer_url) {
      referrer = 'direct';
    } else if (referrer_url.indexOf('/t.co/') > -1) {
      referrer = 'twitter';
    } else if (referrer_url.indexOf('google.') > -1) {
      referrer = 'google';
    } else if (referrer_url.indexOf('bing.') > -1) {
      referrer = 'bing';
    } else if (referrer_url.indexOf('facebook.') > -1) {
      referrer = 'facebook';
    } else {
      referrer = 'other';
    }
  } catch (e) {
    // just let referrer be unknown
  }
  const type = getLoginType(creds);
  const hybridTier = loggingData === Object(loggingData) ? loggingData.hybridTier : 'tier_unknown';
  const container =
    loggingData === Object(loggingData) ? loggingData.container : 'container_unknown';
  trackRegisterAction('login.referrer.' + referrer + '.' + type);
  trackRegisterAction('login.container.' + container + '.' + type);
  trackRegisterAction('login.type.' + type);
  const method = getLoginType(creds) || 'method_unknown';
  const page = loggingData.page || 'page_unknown';
  if (creds.gplus_autologin) {
    trackRegisterAction('web_autologin_google');
  } else if (creds.facebook_autologin) {
    trackRegisterAction('web_autologin_facebook');
  } else {
    trackRegisterAction(
      `web_login.${method}.success.${page}.${container}.${referrer}.${hybridTier}`,
    );
  }
};

const trackCrossDomainLogin = (action: string, creds: LoginCreds) => {
  trackRegisterAction(`web_cross_domain_login.${action}.${getLoginType(creds)}`);
};

const shouldPerformCrossDomainLogin = function(creds: LoginCreds) {
  // two-factor auth resend code do not support/need cross domain login
  if (creds.mfa_resend) {
    return false;
  }

  // note on two-factor auth: it supports cross domain login, but the decision of whether we should
  // perform it or not depends on the underlying login method (username_or_email, facebook, google).
  if (creds.username_or_email) {
    return true;
  }

  return false;
};

const crossDomainLogin = function(creds: LoginCreds, loggingData: LoggingData) {
  trackCrossDomainLogin('attempt', creds);
  return AuthHandshake.loginUser(creds).then(
    (response) => {
      if (response && response.data) {
        trackCrossDomainLogin('success_with_token', creds);
        const token = response.data;
        return AuthHandshake.exchangeTokenAndSetSession(token).then(
          (response) => {
            trackCrossDomainLogin('success_token_exchanged', creds);
            trackLogin(creds, loggingData);
            authHandshakeLog.debug('Cross domain login successful');
            return Promise.resolve();
          },
          (error) => {
            trackCrossDomainLogin('success_token_not_exchanged', creds);
            authHandshakeLog.error('Failed to set session for current user; ' + error.message);
            throw error;
          },
        );
      } else {
        trackCrossDomainLogin('success_without_token', creds);
        authHandshakeLog.error('No custom access token in cross domain login response');
        throw new Error();
      }
    },
    (error) => {
      trackCrossDomainLogin('failure', creds);
      trackLoginInFailure(error, loggingData, getLoginType(creds), true);
      authHandshakeLog.error('Failed to login user; ' + error.message);
      throw error;
    },
  );
};

const login = function(
  creds: LoginCreds,
  loggingData: $Shape<LoggingData> = {},
  expClient: ?UnauthSeoExperiments = null,
  isAccountSwitch: boolean = false,
  recaptchaToken?: string,
  tokenV3?: string,
) {
  if (shouldPerformCrossDomainLogin(creds)) {
    const handshakeDomains = templateConst.settings.CORS_HANDSHAKE_DOMAINS;
    if (handshakeDomains.indexOf(context.instance.origin) !== -1) {
      return crossDomainLogin(creds, loggingData);
    }
  }

  if (creds.switch_account) {
    creds.get_user = isAccountSwitch ? true : creds.get_user;
  }

  const visitedPagesBeforeLogin = isAccountSwitch
    ? null
    : storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_PAGES_BEFORE_LOGIN);

  const resourceOptions = {
    ...creds,
    app_type_from_client: PCONST.thrift.AppType.WEB_DENZEL,
    visited_pages_before_login: visitedPagesBeforeLogin
      ? JSON.stringify(visitedPagesBeforeLogin)
      : null,
    recaptchaToken,
    tokenV3,
  };

  const resource = ResourceFactory.create('UserSessionResource', resourceOptions);

  const resourceCallOptions = {
    showError: false,
    async: true,
  };

  return resource.callCreate(resourceCallOptions).then(
    (resourceResponse) => {
      log.debug('Login, successful', resourceResponse);
      trackLogin(creds, loggingData);

      setUserOpenUnauth(false);

      return unregisterServiceWorker('login').then(() => resourceResponse);
    },
    (error) => {
      trackLoginInFailure(error, loggingData, getLoginType(creds));
      return Promise.reject(error);
    },
  );
};

export default login;
